import React, { createContext, useContext, useEffect, useState } from "react";
import { googleLogout } from "@react-oauth/google";
import fetchWithCredentials, { FetchWithCredentialsError } from "apiClient";
import { isNotAManagerError, setLoginError } from "auth/loginErrors";
import { disableAutoSignIn } from "auth/autoSignIn";
import Spinner from "components/ui/Spinner";
import { Manager } from "types/Manager";
import { LegalDocumentPayload } from "types/Legal";

type AuthContextType = {
  isAuthenticated: boolean;
  managerId: number | null;
  managerName: string | null;
  managerCountry: string | null;
  lang: string | null;
  hasPartner: boolean;
  partnerName: string | null;
  email: string | null;
  isSuperuser: boolean;
  isAdmin: boolean;
  isImpersonating: boolean;
  impersonatedManager: Manager | null;
  subscriptionExpiresAt: string | null;
  subscriptionStatus: string | null;
  activationVariation: string | null;
  wappTokenReconnectRecommended: boolean;
  wappDisconnected: boolean;
  wappTokenExpired: boolean;
  hasWappConnectedNumber: boolean;
  hasPhone: boolean;
  needsTermsAcceptance: boolean;
  currentTermsDocument: LegalDocumentPayload | null;
  isDemo: boolean;
  refetchProfile: () => Promise<void>;
  logout: () => void;
  startImpersonation: (managerUserId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  getImpersonationStatus: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  managerId: null,
  managerName: null,
  managerCountry: null,
  lang: "en",
  hasPartner: false,
  partnerName: null,
  email: null,
  isSuperuser: false,
  isAdmin: false,
  isImpersonating: false,
  impersonatedManager: null,
  subscriptionExpiresAt: null,
  subscriptionStatus: null,
  activationVariation: null,
  wappTokenReconnectRecommended: false,
  wappDisconnected: false,
  wappTokenExpired: false,
  hasWappConnectedNumber: false,
  hasPhone: true,
  needsTermsAcceptance: false,
  currentTermsDocument: null,
  isDemo: false,
  refetchProfile: async () => {},
  logout: () => {},
  startImpersonation: async () => {},
  stopImpersonation: async () => {},
  getImpersonationStatus: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [managerId, setManagerId] = useState<number | null>(null);
  const [managerName, setManagerName] = useState<string | null>(null);
  const [managerCountry, setManagerCountry] = useState<string | null>(null);
  const [lang, setLang] = useState<string | null>(null);
  const [hasPartner, setHasPartner] = useState<boolean>(false);
  const [partnerName, setPartnerName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isSuperuser, setIsSuperuser] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [impersonatedManager, setImpersonatedManager] =
    useState<Manager | null>(null);
  const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState<
    string | null
  >(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(
    null
  );
  const [activationVariation, setActivationVariation] = useState<string | null>(
    null
  );
  const [wappTokenReconnectRecommended, setWappTokenReconnectRecommended] =
    useState(false);
  const [wappDisconnected, setWappDisconnected] = useState(false);
  const [wappTokenExpired, setWappTokenExpired] = useState(false);
  const [hasWappConnectedNumber, setHasWappConnectedNumber] = useState(false);
  const [hasPhone, setHasPhone] = useState(true);
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);
  const [currentTermsDocument, setCurrentTermsDocument] =
    useState<LegalDocumentPayload | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const getImpersonationStatus = async () => {
    try {
      const status = await fetchWithCredentials("/api/impersonation-status/");
      setIsImpersonating(status.impersonating);
      if (status.impersonating && status.userId) {
        const manager = await fetchWithCredentials(
          "/manager-api/current-manager/"
        );
        setImpersonatedManager(manager || null);
      } else {
        setImpersonatedManager(null);
      }
    } catch (err) {
      console.error("Failed to get impersonation status:", err);
    }
  };

  const startImpersonation = async (managerUserId: number) => {
    const payload = { user_id: managerUserId };

    try {
      await fetchWithCredentials("/api/impersonate/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      await getImpersonationStatus();
      // Refresh the page to update all contexts
      window.location.reload();
    } catch (err) {
      console.error("Failed to start impersonation:", err);
      throw err;
    }
  };

  const stopImpersonation = async () => {
    try {
      await fetchWithCredentials("/api/stop-impersonate/", {
        method: "POST",
      });
      await getImpersonationStatus();
      // Refresh the page to update all contexts
      window.location.reload();
    } catch (err) {
      console.error("Failed to stop impersonation:", err);
      throw err;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setLoaded(true);
      return;
    }

    async function fetchProfile() {
      try {
        const data = await fetchWithCredentials(
          "/manager-api/manager/profile/"
        );
        setManagerId(data.id);
        setManagerName(data.name);
        setManagerCountry(data.country);
        setLang(data.lang || null);
        setHasPartner(data.hasPartner || false);
        setPartnerName(data.partnerName || null);
        setEmail(data.email);
        setSubscriptionExpiresAt(data.subscriptionExpiresAt || null);
        setSubscriptionStatus(data.subscriptionStatus || null);
        setActivationVariation(data.activationVariation || null);
        setWappTokenReconnectRecommended(
          Boolean(data.wappTokenReconnectRecommended),
        );
        setWappDisconnected(Boolean(data.wappDisconnected));
        setWappTokenExpired(Boolean(data.wappTokenExpired));
        setHasWappConnectedNumber(Boolean(data.wappConnectedNumber));
        setHasPhone(Boolean(data.hasPhone));
        setIsAdmin(data.isAdmin || false);
        setNeedsTermsAcceptance(Boolean(data.needsTermsAcceptance));
        setCurrentTermsDocument(data.currentTermsDocument ?? null);
        setIsDemo(Boolean(data.isDemo));

        // Check if user is superuser by trying to access managers list (only if terms accepted)
        if (!data.needsTermsAcceptance) {
          try {
            await fetchWithCredentials("/manager-api/all-managers/");
            setIsSuperuser(true);
          } catch {
            setIsSuperuser(false);
          }
          await getImpersonationStatus();
        }
      } catch (err) {
        console.error("Auth profile fetch failed:", err);
        if (isNotAManagerError(err)) {
          setLoginError("not_a_manager");
          logout();
        } else if (
          err instanceof FetchWithCredentialsError &&
          err.status === 401
        ) {
          // Genuine auth rejection — clear the session.
          // Do not treat generic 403s (terms, permissions) as logout.
          logout();
        }
        // Transient network/5xx errors keep tokens so a reload can recover.
      } finally {
        setLoaded(true);
      }
    }

    fetchProfile();
  }, []);

  const refetchProfile = async () => {
    setNeedsTermsAcceptance(false);
    setCurrentTermsDocument(null);
    try {
      const data = await fetchWithCredentials(
        "/manager-api/manager/profile/"
      );
      setManagerId(data.id);
      setManagerName(data.name);
      setManagerCountry(data.country);
      setLang(data.lang || null);
      setHasPartner(data.hasPartner || false);
      setPartnerName(data.partnerName || null);
      setEmail(data.email);
      setSubscriptionExpiresAt(data.subscriptionExpiresAt || null);
      setSubscriptionStatus(data.subscriptionStatus || null);
      setActivationVariation(data.activationVariation || null);
      setWappTokenReconnectRecommended(
        Boolean(data.wappTokenReconnectRecommended),
      );
      setWappDisconnected(Boolean(data.wappDisconnected));
      setWappTokenExpired(Boolean(data.wappTokenExpired));
      setHasWappConnectedNumber(Boolean(data.wappConnectedNumber));
      setHasPhone(Boolean(data.hasPhone));
      setIsAdmin(data.isAdmin || false);
      setNeedsTermsAcceptance(Boolean(data.needsTermsAcceptance));
      setCurrentTermsDocument(data.currentTermsDocument ?? null);
      setIsDemo(Boolean(data.isDemo));
      if (!data.needsTermsAcceptance) {
        try {
          await fetchWithCredentials("/manager-api/all-managers/");
          setIsSuperuser(true);
        } catch {
          setIsSuperuser(false);
        }
        await getImpersonationStatus();
      }
    } catch (err) {
      console.error("Refetch profile failed:", err);
    }
  };

  useEffect(() => {
    const onTermsRequired = (e: Event) => {
      const customEvent = e as CustomEvent<LegalDocumentPayload>;
      if (customEvent.detail) {
        setCurrentTermsDocument(customEvent.detail);
        setNeedsTermsAcceptance(true);
      }
    };
    window.addEventListener("terms-required", onTermsRequired);
    return () => window.removeEventListener("terms-required", onTermsRequired);
  }, []);

  const logout = () => {
    googleLogout();
    disableAutoSignIn();
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setManagerId(null);
    setManagerName(null);
    setManagerCountry(null);
    setHasPartner(false);
    setPartnerName(null);
    setEmail(null);
    setIsSuperuser(false);
    setIsAdmin(false);
    setIsImpersonating(false);
    setImpersonatedManager(null);
    setSubscriptionExpiresAt(null);
    setSubscriptionStatus(null);
    setActivationVariation(null);
    setWappTokenReconnectRecommended(false);
    setWappDisconnected(false);
    setWappTokenExpired(false);
    setHasWappConnectedNumber(false);
    setHasPhone(true);
    setNeedsTermsAcceptance(false);
    setCurrentTermsDocument(null);
    setIsDemo(false);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: Boolean(managerId),
        managerId,
        managerName,
        managerCountry,
        lang,
        hasPartner,
        partnerName,
        email,
        isSuperuser,
        isAdmin,
        isImpersonating,
        impersonatedManager,
        subscriptionExpiresAt,
        subscriptionStatus,
        activationVariation,
        wappTokenReconnectRecommended,
        wappDisconnected,
        wappTokenExpired,
        hasWappConnectedNumber,
        hasPhone,
        needsTermsAcceptance,
        currentTermsDocument,
        isDemo,
        refetchProfile,
        logout,
        startImpersonation,
        stopImpersonation,
        getImpersonationStatus,
      }}
    >
      {loaded ? children : <Spinner className="h-screen" />}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
